import { ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';

import { getMappedNotesArrByType, PartNoteType } from 'bom/components/AddPartNote';
import { NotesCompactView } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { INote } from 'core/types';
import { getAgGridTableCellData } from 'core/utils';
import { AddNoteButton } from 'partBoms/components/PartBomDetails';
import { usePartBomItemNoteActions } from 'partBoms/hooks';
import { IPartBomItem, IPartBomItemNote, IUpsertPartBomItemNote } from 'partBoms/types';
import { Variant } from 'theme/enums';

interface IProps extends ICellRendererParams {
  isRowEditable: ((data: IPartBomItem) => boolean) | boolean;
}

export const NotesCell = ({ isRowEditable = false, ...props }: IProps) => {
  const [isOpenManageNotes, setIsOpenManageNotes] = useState(false);

  const { clearNotification } = useNotificationContext();
  const { updateRows } = useAgGridClientTableContext();

  const { removePartBomItemNotes, upsertPartBomItemNote } = usePartBomItemNoteActions({
    notificationPosition: NotificationPosition.DRAWER,
  });

  const data = getAgGridTableCellData(props, 'notes');

  const mappedNotes = useMemo(
    () => getMappedNotesArrByType(Object.values(data?.notes || {}), PartNoteType.DEFAULT),
    [data]
  );

  const onCellDoubleClicked = () => setIsOpenManageNotes(true);

  useEffect(() => {
    const handleDoubleClick = (eventParams) => {
      if (
        eventParams.node.rowIndex === props.node.rowIndex &&
        eventParams.column.colId === props.column.getColId()
      ) {
        onCellDoubleClicked();
      }
    };

    props.api.addEventListener('cellDoubleClicked', handleDoubleClick);

    return () => {
      !props.api.isDestroyed() &&
        props.api.removeEventListener('cellDoubleClicked', handleDoubleClick);
    };
  }, [props.api, props.column, props.node.rowIndex]);

  if (!data) {
    return props.value;
  }

  const editable = typeof isRowEditable === 'function' ? isRowEditable(data) : isRowEditable;

  const onSubmitEditNote = async (data: Partial<INote>) => {
    const { success, partBomItemNote } = await upsertPartBomItemNote({
      partBomItemNote: data as IUpsertPartBomItemNote,
    });

    if (success) {
      setTimeout(() => {
        updatePartBomItem(partBomItemNote);
        clearNotification();
      }, 1000);
    }

    return { success };
  };

  const onDeleteNote = async (note: Partial<INote>) => {
    const { success } = await removePartBomItemNotes({
      ids: [note.id],
    });

    if (!success) {
      return;
    }

    setTimeout(() => {
      updatePartBomItem(undefined, note.id);
      clearNotification();
    }, 1000);
  };

  const updatePartBomItem = (note?: IPartBomItemNote, deleteNoteId?: number) => {
    const prevNotes = data.notes;

    const updatedNotes: Record<string, IPartBomItemNote> = { ...prevNotes };

    if (deleteNoteId) {
      delete updatedNotes[deleteNoteId];
    }

    if (note) {
      updatedNotes[note.id] = note;
    }

    updateRows({
      update: [
        {
          ...data,
          notes: updatedNotes,
        },
      ],
    });
  };

  return (
    <NotesCompactView
      compactPartNotes={mappedNotes.slice(0, 2)}
      totalNotes={mappedNotes}
      hasManageNotes={editable}
      manageNotesIsOpenExternal={isOpenManageNotes}
      updateManageNotesIsOpenExternal={setIsOpenManageNotes}
      addButton={<AddNoteButton partBomItem={data} level={1} />}
      listAddButton={
        <AddNoteButton
          partBomItem={data}
          buttonProps={{ startDecorator: null, variant: Variant.PLAIN }}
        />
      }
      noteProps={{ onSubmitEditNote, onDeleteNote }}
    />
  );
};
